const API_BASE = import.meta.env.VITE_API_URL || '';

const resolveTenantCode = (tenant) =>
  tenant?.code || tenant?.codigo || tenant?.tenantCode || tenant?.id || tenant?.nombre || '';

const buildHeaders = ({ tenant, token } = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  const tenantCode = resolveTenantCode(tenant);
  if (tenantCode) headers['X-Tenant'] = tenantCode;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const handleResponse = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    const message = payload?.message || payload?.error || fallbackMessage;
    const error = new Error(message);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  return payload.data;
};

export async function fetchFacturas({ tenant, token, sucursalId, clienteId, filters = {}, page, pageSize } = {}) {
  const params = new URLSearchParams();
  const resolvedSucursalId = filters.sucursalId ?? sucursalId;
  const resolvedClienteId = filters.clienteId ?? clienteId;
  if (resolvedSucursalId) params.set('sucursal_id', resolvedSucursalId);
  if (resolvedClienteId) params.set('cliente_id', resolvedClienteId);
  if (filters.search) params.set('search', filters.search);
  if (filters.cliente) params.set('cliente', filters.cliente);
  if (filters.desde) params.set('desde', filters.desde);
  if (filters.hasta) params.set('hasta', filters.hasta);
  if (filters.tipo) params.set('tipo', filters.tipo);
  if (filters.estado) params.set('estado', filters.estado);
  if (filters.saldo) params.set('saldo', filters.saldo);
  if (filters.origen) params.set('origen', filters.origen);
  if (filters.condicionVenta) params.set('condicion_venta', filters.condicionVenta);
  if (filters.usuarioId) params.set('usuario_id', filters.usuarioId);
  if (filters.puntoVenta) params.set('punto_venta', filters.puntoVenta);
  if (filters.cae === 'con_cae') params.set('con_cae', '1');
  if (filters.cae === 'sin_cae') params.set('sin_cae', '1');
  if (filters.anuladas === '1') params.set('anuladas', '1');
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  if (page) params.set('page', page);
  if (pageSize) params.set('page_size', pageSize);
  const response = await fetch(
    params.toString() ? `${API_BASE}/api/facturas?${params.toString()}` : `${API_BASE}/api/facturas`,
    { headers: buildHeaders({ tenant, token }) },
  );
  return handleResponse(response, 'No se pudieron cargar las facturas.');
}

export async function exportFacturas({ tenant, token, filters = {}, selection = [] } = {}) {
  const params = new URLSearchParams({ export: 'csv' });
  const mappings = {
    sucursalId: 'sucursal_id', clienteId: 'cliente_id', search: 'search', cliente: 'cliente',
    desde: 'desde', hasta: 'hasta', tipo: 'tipo', estado: 'estado', saldo: 'saldo',
    origen: 'origen', condicionVenta: 'condicion_venta', usuarioId: 'usuario_id',
    puntoVenta: 'punto_venta', sort: 'sort', order: 'order',
  };
  Object.entries(mappings).forEach(([key, param]) => {
    if (filters[key]) params.set(param, filters[key]);
  });
  if (filters.cae === 'con_cae') params.set('con_cae', '1');
  if (filters.cae === 'sin_cae') params.set('sin_cae', '1');
  if (filters.anuladas === '1') params.set('anuladas', '1');
  if (selection.length) params.set('ids', selection.join(','));

  const response = await fetch(`${API_BASE}/api/facturas?${params.toString()}`, {
    headers: buildHeaders({ tenant, token }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.message || payload?.error || 'No se pudo exportar la consulta.');
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `facturas-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export async function fetchFacturables({ tenant, token, filters = {} } = {}) {
  const params = new URLSearchParams();
  if (filters.sucursalId) params.set('sucursal_id', filters.sucursalId);
  if (filters.clienteId) params.set('cliente_id', filters.clienteId);
  if (filters.fechaDesde) params.set('fecha_desde', filters.fechaDesde);
  if (filters.fechaHasta) params.set('fecha_hasta', filters.fechaHasta);
  if (filters.condicionCobro) params.set('condicion_cobro', filters.condicionCobro);
  if (filters.estadoOperativo) params.set('estado_operativo', filters.estadoOperativo);
  if (filters.search) params.set('search', filters.search);
  if (filters.soloContadoPendiente) params.set('solo_contado_pendiente', '1');

  const response = await fetch(`${API_BASE}/api/facturas/pendientes/guias?${params.toString()}`, {
    headers: buildHeaders({ tenant, token }),
  });
  return handleResponse(response, 'No se pudieron cargar las guías facturables.');
}

export async function resolveFacturaDesdeGuiasConfiguration({ tenant, token, clienteId } = {}) {
  const params = new URLSearchParams({ cliente_id: String(clienteId || '') });
  const response = await fetch(`${API_BASE}/api/facturas/desde-guias/configuracion?${params.toString()}`, {
    headers: buildHeaders({ tenant, token }),
  });
  return handleResponse(response, 'No se pudo resolver la configuración fiscal del cliente.');
}

export async function previewFacturaDesdeGuias({ tenant, token, payload }) {
  const response = await fetch(`${API_BASE}/api/facturas/preview-desde-guias`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'No se pudo construir la previsualización.');
}

export async function createFactura({ tenant, token, payload }) {
  const response = await fetch(`${API_BASE}/api/facturas/emitir-desde-guias`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'No se pudo emitir la factura.');
}

export async function fetchFacturaDetail({ tenant, token, id }) {
  const response = await fetch(`${API_BASE}/api/facturas/${id}`, {
    headers: buildHeaders({ tenant, token }),
  });
  return handleResponse(response, 'No se pudo obtener la factura.');
}

export async function discardFacturaDraft({ tenant, token, id }) {
  const response = await fetch(`${API_BASE}/api/facturas/manual/borradores/${id}`, {
    method: 'DELETE', headers: buildHeaders({ tenant, token }),
  });
  return handleResponse(response, 'No se pudo descartar el borrador.');
}

export async function anularFactura({ tenant, token, id, motivo, observaciones }) {
  const response = await fetch(`${API_BASE}/api/facturas/${id}/anular`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify({ motivo, observaciones }),
  });
  return handleResponse(response, 'No se pudo anular la factura.');
}

// Level-1000 only: compare a draft with a voucher AFIP authorized (no writes).
export async function previewFacturaAfipRecovery({ tenant, token, id, numero }) {
  const response = await fetch(`${API_BASE}/api/facturas/${id}/recuperar-afip/consultar`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify({ numero }),
  });
  return handleResponse(response, 'No se pudo consultar el comprobante en AFIP.');
}

// Level-1000 only: record the draft as the invoice AFIP authorized.
export async function recoverFacturaFromAfip({ tenant, token, id, numero }) {
  const response = await fetch(`${API_BASE}/api/facturas/${id}/recuperar-afip`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify({ numero }),
  });
  return handleResponse(response, 'No se pudo recuperar la factura desde AFIP.');
}

export async function sendFacturaEmail({ tenant, token, id, to, cc, subject, message }) {
  const response = await fetch(`${API_BASE}/api/facturas/${id}/enviar-email`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify({ to, cc, subject, message }),
  });
  return handleResponse(response, 'No se pudo enviar la factura por correo.');
}

export async function fetchInvoicePrintConfiguration({ tenant, token }) {
  const response = await fetch(`${API_BASE}/api/facturas/configuracion-impresion`, { headers: buildHeaders({ tenant, token }) });
  return handleResponse(response, 'No se pudo cargar la configuración de impresión.');
}

export async function saveInvoicePrintConfiguration({ tenant, token, detailColumns, designer }) {
  const response = await fetch(`${API_BASE}/api/facturas/configuracion-impresion`, {
    method: 'PUT', headers: buildHeaders({ tenant, token }), body: JSON.stringify({ detailColumns, designer }),
  });
  return handleResponse(response, 'No se pudo guardar la configuración de impresión.');
}

export async function previewFacturaPdf({ tenant, token, id }) {
  // Abrir la ventana durante el gesto del usuario evita bloqueadores de popups;
  // el PDF se carga luego como blob para conservar Authorization y X-Tenant.
  const popup = window.open('', '_blank');
  if (!popup) throw new Error('El navegador bloqueó la apertura del PDF.');
  try {
    popup.document.write('<title>Factura PDF</title><p style="font-family:system-ui;padding:24px">Cargando PDF…</p>');
    popup.document.close();
    const response = await fetch(`${API_BASE}/api/facturas/${id}/pdf`, {
      headers: buildHeaders({ tenant, token }),
      credentials: 'include',
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.message || payload?.error || 'No se pudo abrir el PDF de la factura.');
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    popup.location.replace(objectUrl);
    popup.addEventListener('beforeunload', () => window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000), { once: true });
  } catch (error) {
    popup.close();
    throw error;
  }
}

export async function downloadFacturaPdf({ tenant, token, id, fileName = `factura-${id}.pdf` }) {
  const response = await fetch(`${API_BASE}/api/facturas/${id}/pdf`, {
    headers: buildHeaders({ tenant, token }),
    credentials: 'include',
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.message || payload?.error || 'No se pudo descargar el PDF de la factura.');
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export async function fetchFacturaDeGuia({ tenant, token, guiaId }) {
  const response = await fetch(`${API_BASE}/api/facturas/guia/${guiaId}/factura`, {
    headers: buildHeaders({ tenant, token }),
  });
  return handleResponse(response, 'No se pudo obtener la factura asociada a la guía.');
}
